'use client';

import React, { useState, useRef, useEffect, useMemo } from 'react';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faSliders, faCaretDown } from '@fortawesome/pro-solid-svg-icons';
import { LOCATION } from 'src/hooks/filters/constants';
import useStrategies from 'src/hooks/strategy/useStrategies';
import { countSocSelections } from 'src/components/FilterSets/SiteOfCare/utils';

import { StrategyStatusDropdown } from '../../../StrategyFilters/StrategyStatusDropdown';
import { StrategySpecialtyDropdown } from '../../../StrategyFilters/StrategySpecialtyDropdown';
import { StrategySiteOfCareTypeDropdown } from '../../../StrategyFilters/StrategySiteOfCareTypeDropdown';
import { StrategyProviderListDropdown } from '../../../StrategyFilters/StrategyProviderListDropdown';

const StrategyFilterButton = () => {
  const {
    selectedSpecialties,
    selectedSocTypes,
    selectedSocSubtypes,
    selectedStatuses,
    selectedProviderLists,
  } = useStrategies(LOCATION.STRATEGY_CENTER);

  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Check if click is on the trigger button
      if (
        buttonRef.current &&
        buttonRef.current.contains(event.target as Node)
      ) {
        return;
      }

      // Check if click is inside the dropdown content
      if (
        dropdownRef.current &&
        dropdownRef.current.contains(event.target as Node)
      ) {
        return;
      }

      // Check if click is inside any portal dropdown
      const isInsideAnyDropdown = (event.target as Element).closest(
        '[data-dropdown-portal]'
      );
      if (isInsideAnyDropdown) {
        return;
      }

      // Click is truly outside, close the dropdown
      setIsOpen(false);
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Handle window resize to close dropdown
  useEffect(() => {
    const handleResize = () => {
      setIsOpen(false);
    };

    if (isOpen) {
      window.addEventListener('resize', handleResize);
    }

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [isOpen]);

  const handleClick = () => {
    setIsOpen(!isOpen);
  };

  const filterCount = useMemo(() => {
    return (
      selectedProviderLists?.length +
      selectedSpecialties?.length +
      // The SOC arrays are one picker: a subtype selection also carries its
      // parent type, so adding both lengths double-counted it.
      countSocSelections(selectedSocTypes, selectedSocSubtypes) +
      selectedStatuses?.length
    );
  }, [
    selectedProviderLists,
    selectedSpecialties,
    selectedSocTypes,
    selectedSocSubtypes,
    selectedStatuses,
  ]);

  return (
    <div className="flex items-start relative">
      <div
        ref={buttonRef}
        className="max-w-48 px-3.5 py-2 bg-white rounded-full outline outline-1 
        -outline-offset-1 outline-neutral-200 flex justify-center items-center gap-2.5 hover:bg-neutral-100 cursor-pointer"
        onClick={handleClick}
      >
        <div className="flex  items-center gap-1">
          <div className="w-4 h-4 flex items-center justify-center">
            <Icon icon={faSliders} className="text-primary" size="sm" />
          </div>
          <div className=" text-primary text-sm font-semibold font-['Inter'] leading-tight">
            Filters
          </div>
          <div
            className={`px-1 py-0.5  rounded-full flex justify-center items-center gap-0.5 overflow-hidden
            ${filterCount > 0 ? 'bg-sky-50' : 'bg-neutral-100'}
            `}
          >
            <div
              className={`text-center text-xs  font-['Inter'] leading-none
              ${
                filterCount > 0
                  ? 'text-sky-600 font-semibold'
                  : 'text-tertiary font-normal'
              }
              `}
            >
              {filterCount || 0}
            </div>
          </div>
          <div className="w-4 h-4 flex items-center justify-center">
            <Icon icon={faCaretDown} className="text-primary" size="sm" />
          </div>
        </div>
      </div>
      {isOpen && (
        <div
          ref={dropdownRef}
          className="absolute mt-1 z-50 top-full left-0 w-64 bg-white rounded-xl shadow-md outline outline-1 -outline-offset-1 outline-neutral-200 inline-flex flex-col justify-start items-start gap-2"
        >
          <div className="self-stretch p-3 flex flex-col justify-start items-start gap-2">
            <StrategyStatusDropdown />
            <StrategySpecialtyDropdown />
            <StrategySiteOfCareTypeDropdown />
            <StrategyProviderListDropdown />
          </div>
        </div>
      )}
    </div>
  );
};

export default StrategyFilterButton;
